import {
  Color3,
  CreateBox,
  CreateCylinder,
  CreatePlane,
  DynamicTexture,
  Mesh,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3
} from "../../app/babylon";
import type { Slope } from "./Slope";

const FLAG_COUNT = 11;
const FLAG_SIZE = 0.42;
const POLE_HEIGHT = 7.5;

export class FinishLine {
  readonly root: TransformNode;
  readonly z: number;
  private flags: { node: TransformNode; phase: number }[] = [];
  private mainBanner: Mesh;
  private bannerStart: number;

  constructor(scene: Scene, slope: Slope, finishZ: number) {
    this.z = finishZ;
    this.root = new TransformNode("finish-line", scene);
    this.bannerStart = performance.now();

    const sample = slope.sampleAtZ(finishZ);
    const sideHalf = slope.groomedHalfWidth + 0.5;

    const checkerTex = createCheckerTexture(scene, "finish-checker", 8, 2);
    const checkerMat = new StandardMaterial("finish-checker-mat", scene);
    checkerMat.diffuseTexture = checkerTex;
    checkerMat.specularColor = new Color3(0.1, 0.1, 0.1);
    checkerMat.backFaceCulling = false;
    checkerMat.emissiveColor = new Color3(0.12, 0.12, 0.12);

    const flagTex = createCheckerTexture(scene, "finish-flag-tex", 4, 4);
    const flagMat = new StandardMaterial("finish-flag-mat", scene);
    flagMat.diffuseTexture = flagTex;
    flagMat.specularColor = new Color3(0.05, 0.05, 0.05);
    flagMat.backFaceCulling = false;
    flagMat.emissiveColor = new Color3(0.1, 0.1, 0.1);

    const lineMat = new StandardMaterial("finish-line-mat", scene);
    lineMat.diffuseColor = new Color3(0.95, 0.1, 0.15);
    lineMat.emissiveColor = new Color3(0.6, 0.06, 0.09);
    lineMat.specularColor = new Color3(0, 0, 0);
    lineMat.alpha = 0.55;

    const line = CreateBox(
      "finish-line",
      { width: slope.powderHalfWidth * 2, height: 0.02, depth: 0.4 },
      scene
    );
    line.material = lineMat;
    line.isPickable = false;
    line.position.set(sample.centerX, sample.surfaceY + 0.015, finishZ);
    line.parent = this.root;

    const poleMat = new StandardMaterial("finish-pole-mat", scene);
    poleMat.diffuseColor = new Color3(0.95, 0.95, 0.95);
    poleMat.specularColor = new Color3(0.5, 0.5, 0.5);

    const buildPole = (sideSign: number): Vector3 => {
      const px = sample.centerX + sample.rightX * sideHalf * sideSign;
      const pz = finishZ + sample.rightZ * sideHalf * sideSign;
      const py = sample.surfaceY + POLE_HEIGHT / 2;
      const pole = CreateCylinder(
        `finish-pole-${sideSign > 0 ? "right" : "left"}`,
        { height: POLE_HEIGHT, diameter: 0.18 },
        scene
      );
      pole.material = poleMat;
      pole.isPickable = false;
      pole.position.set(px, py, pz);
      pole.parent = this.root;
      return new Vector3(px, sample.surfaceY + POLE_HEIGHT, pz);
    };

    const leftTop = buildPole(-1);
    const rightTop = buildPole(1);

    this.mainBanner = CreateBox(
      "finish-banner",
      { width: sideHalf * 2, height: 0.85, depth: 0.08 },
      scene
    );
    this.mainBanner.material = checkerMat;
    this.mainBanner.isPickable = false;
    this.mainBanner.position.set(
      sample.centerX,
      sample.surfaceY + POLE_HEIGHT - 0.43,
      finishZ
    );
    this.mainBanner.parent = this.root;

    const ropeY = sample.surfaceY + POLE_HEIGHT - 1.05;
    const rope = CreateCylinder(
      "finish-rope",
      { height: sideHalf * 2, diameter: 0.045 },
      scene
    );
    const ropeMat = new StandardMaterial("finish-rope-mat", scene);
    ropeMat.diffuseColor = new Color3(0.08, 0.08, 0.08);
    rope.material = ropeMat;
    rope.isPickable = false;
    rope.rotation.z = Math.PI / 2;
    rope.position.set(sample.centerX, ropeY, finishZ);
    rope.parent = this.root;

    for (let i = 0; i < FLAG_COUNT; i += 1) {
      const fraction = (i + 0.5) / FLAG_COUNT;
      const x = leftTop.x + (rightTop.x - leftTop.x) * fraction;
      const z = leftTop.z + (rightTop.z - leftTop.z) * fraction;

      const pivot = new TransformNode(`finish-flag-pivot-${i}`, scene);
      pivot.position.set(x, ropeY, z);
      pivot.parent = this.root;

      const flag = CreatePlane(
        `finish-flag-${i}`,
        { width: FLAG_SIZE, height: FLAG_SIZE * 1.1 },
        scene
      );
      flag.material = flagMat;
      flag.isPickable = false;
      flag.position.set(0, -FLAG_SIZE * 0.55, 0);
      flag.parent = pivot;

      this.flags.push({ node: pivot, phase: i * 0.6 });
    }
  }

  update(): void {
    const t = (performance.now() - this.bannerStart) / 1000;
    for (const { node, phase } of this.flags) {
      const swing = Math.sin(t * 2.4 + phase) * 0.5;
      const flutter = Math.sin(t * 5 + phase * 1.7) * 0.18;
      node.rotation.x = swing;
      node.rotation.z = flutter;
    }
  }

  hasCrossed(point: Vector3): boolean {
    return point.z >= this.z;
  }
}

function createCheckerTexture(
  scene: Scene,
  name: string,
  cellsX: number,
  cellsY: number
): DynamicTexture {
  const size = 256;
  const texture = new DynamicTexture(name, { width: size, height: size }, scene, false);
  const ctx = texture.getContext();
  const cellW = size / cellsX;
  const cellH = size / cellsY;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#0a0a0a";
  for (let y = 0; y < cellsY; y += 1) {
    for (let x = 0; x < cellsX; x += 1) {
      if ((x + y) % 2 === 0) {
        ctx.fillRect(x * cellW, y * cellH, cellW, cellH);
      }
    }
  }
  texture.update();
  return texture;
}
